import { supabase } from "@/integrations/supabase/client";

/**
 * Default numbering templates — used when no numbering_config row exists.
 */
const DEFAULT_TEMPLATES = {
  wo_template: "WO-{TYPE}-{PROJECT}-{VENDOR}-{SEQ}",
  bill_template: "Bill-{TYPE}-{PROJECT}-{VENDOR}-{SEQ}",
  po_template: "PO-{PROJECT}-{SEQ}",
};

/**
 * Fetch the company's numbering_config, falling back to defaults.
 */
async function getNumberingConfig(companyId: string) {
  const { data } = await (supabase.from("numbering_config") as any)
    .select("wo_template, bill_template, po_template")
    .eq("company_id", companyId)
    .maybeSingle();
  return {
    wo_template: data?.wo_template || DEFAULT_TEMPLATES.wo_template,
    bill_template: data?.bill_template || DEFAULT_TEMPLATES.bill_template,
    po_template: data?.po_template || DEFAULT_TEMPLATES.po_template,
  };
}

/**
 * Apply a numbering template with token replacement.
 */
function applyTemplate(
  template: string,
  vars: Record<string, string>
): string {
  let result = template;
  for (const [token, value] of Object.entries(vars)) {
    result = result.replace(`{${token}}`, value);
  }
  return result;
}

/**
 * Abbreviate a name by taking the first letter of each word.
 * "General Contracting Firm" → "GCF"
 */
export function abbreviate(name: string): string {
  if (!name) return "UNK";
  const letters = name
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .join("");
  return letters || "UNK";
}

/**
 * Pure format helpers (use defaults, kept for backward compat)
 */
export function formatWONumber(
  typeAbbrev: string,
  projectNumber: string,
  vendorNumber: number,
  sequence: number
): string {
  return applyTemplate(DEFAULT_TEMPLATES.wo_template, {
    TYPE: typeAbbrev,
    PROJECT: projectNumber,
    VENDOR: String(vendorNumber),
    SEQ: String(sequence).padStart(2, "0"),
  });
}

export function formatBillNumber(
  typeAbbrev: string,
  projectNumber: string,
  vendorNumber: number,
  sequence: number
): string {
  return applyTemplate(DEFAULT_TEMPLATES.bill_template, {
    TYPE: typeAbbrev,
    PROJECT: projectNumber,
    VENDOR: String(vendorNumber),
    SEQ: String(sequence).padStart(2, "0"),
  });
}

export function formatPONumber(
  projectNumber: string,
  sequence: number
): string {
  return applyTemplate(DEFAULT_TEMPLATES.po_template, {
    PROJECT: projectNumber,
    SEQ: String(sequence).padStart(3, "0"),
  });
}

/**
 * Fetch project info + vendor number, count existing WOs
 * for project+vendor pair, and return the next WO number.
 * Reads numbering_config for template customization.
 */
export async function generateWONumber(
  projectId: string,
  vendorId?: string,
  _vendorName?: string
): Promise<string> {
  // Fetch project number + type abbreviation + company_id
  const { data: project } = await supabase
    .from("projects")
    .select("project_number, company_id, project_types(abbreviation)")
    .eq("id", projectId)
    .single();

  const projectNumber = (project as any)?.project_number ?? "UNKNOWN";
  const companyId = (project as any)?.company_id;
  const typeAbbrev = (project as any)?.project_types?.abbreviation ?? "XX";

  // Fetch vendor number
  let vendorNumber = 0;
  if (vendorId) {
    const { data: vendor } = await supabase
      .from("vendors")
      .select("vendor_number")
      .eq("id", vendorId)
      .single();
    vendorNumber = (vendor as any)?.vendor_number ?? 0;
  }

  // Find MAX sequence from active (non-voided) WOs for this project+vendor
  // so voided/deleted WOs free up their number for reuse
  let woQuery = supabase
    .from("work_orders")
    .select("number")
    .eq("project_id", projectId)
    .neq("status", "voided");

  if (vendorId) {
    woQuery = woQuery.eq("contractor_id", vendorId);
  }

  const { data: activeWOs } = await woQuery;
  let maxSeq = 0;
  for (const wo of activeWOs ?? []) {
    const parts = ((wo as any).number as string ?? "").split("-");
    const seq = parseInt(parts[parts.length - 1], 10);
    if (!isNaN(seq) && seq > maxSeq) maxSeq = seq;
  }
  const sequence = maxSeq + 1;

  // Read company template
  const config = companyId ? await getNumberingConfig(companyId) : DEFAULT_TEMPLATES;

  return applyTemplate(config.wo_template, {
    TYPE: typeAbbrev,
    PROJECT: projectNumber,
    VENDOR: String(vendorNumber),
    SEQ: String(sequence).padStart(2, "0"),
  });
}

/**
 * Generate a sequential bill number for a given WO.
 * Reads numbering_config for template customization.
 */
export async function generateBillNumber(
  workOrderId: string,
  _woNumber?: string
): Promise<string> {
  // Fetch WO with project info + vendor number
  const { data: wo } = await supabase
    .from("work_orders")
    .select("id, project_id, contractor_id, projects(project_number, company_id, project_types(abbreviation))")
    .eq("id", workOrderId)
    .single();

  const projectNumber = (wo as any)?.projects?.project_number ?? "UNKNOWN";
  const companyId = (wo as any)?.projects?.company_id;
  const typeAbbrev = (wo as any)?.projects?.project_types?.abbreviation ?? "XX";

  // Fetch vendor number from contractor_id
  let vendorNumber = 0;
  const contractorId = (wo as any)?.contractor_id;
  if (contractorId) {
    const { data: vendor } = await supabase
      .from("vendors")
      .select("vendor_number")
      .eq("id", contractorId)
      .single();
    vendorNumber = (vendor as any)?.vendor_number ?? 0;
  }

  // Find the highest sequence number among active (non-voided) bills for this project + vendor.
  // This ensures that if the most recent bill is voided or hard-deleted, the next bill
  // reuses that sequence number rather than skipping ahead.
  let billQuery = supabase
    .from("bills")
    .select("bill_number")
    .eq("project_id", (wo as any)?.project_id)
    .neq("status", "voided");
  if (contractorId) {
    billQuery = billQuery.eq("vendor_id", contractorId);
  }
  const { data: activeBills } = await billQuery;

  let maxSeq = 0;
  for (const b of activeBills ?? []) {
    const parts = (b.bill_number as string).split("-");
    const seq = parseInt(parts[parts.length - 1], 10);
    if (!isNaN(seq) && seq > maxSeq) maxSeq = seq;
  }
  const sequence = maxSeq + 1;

  const config = companyId ? await getNumberingConfig(companyId) : DEFAULT_TEMPLATES;

  return applyTemplate(config.bill_template, {
    TYPE: typeAbbrev,
    PROJECT: projectNumber,
    VENDOR: String(vendorNumber),
    SEQ: String(sequence).padStart(2, "0"),
  });
}

/**
 * Generate a PO number for a project.
 * Reads numbering_config for template customization.
 */
export async function generatePONumber(projectId: string): Promise<string> {
  const { data: project } = await supabase
    .from("projects")
    .select("project_number, company_id")
    .eq("id", projectId)
    .single();

  const projectNumber = (project as any)?.project_number ?? "UNKNOWN";
  const companyId = (project as any)?.company_id;

  const { count } = await supabase
    .from("purchase_orders")
    .select("id", { count: "exact", head: true })
    .eq("project_id", projectId);

  const sequence = (count ?? 0) + 1;

  const config = companyId ? await getNumberingConfig(companyId) : DEFAULT_TEMPLATES;

  return applyTemplate(config.po_template, {
    PROJECT: projectNumber,
    SEQ: String(sequence).padStart(3, "0"),
  });
}
